import {
  Typography,
  Table,
  Button,
  Modal,
  Form,
  Select,
  notification,
  Space,
  Skeleton,
  Tag,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useCallback, useState } from "react";
import {
  useDeleteSalesConfigurationMutation,
  useUserSelectOptions,
  useUpsertSalesConfigurationMutation,
} from "admin/queries";
import { useSalesConfigurations } from "sales/queries";
import { PercentageInput } from "shared/components";
import { formatCommissionRate } from "shared/utils/formatCommissionRate";
import { ISalesConfiguration } from "sales/models";
import { ISelectOption } from "shared/models";
import "./SalesConfigurationSetup.scss";

export const SalesConfigurationSetup = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<ISalesConfiguration | null>(null);
  const [form] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();
  const [api, notificationContextHolder] = notification.useNotification();

  const {
    data: configurationsData,
    isLoading: configurationsLoading,
    isSuccess: configurationsSuccess,
  } = useSalesConfigurations();

  const {
    data: userSelectOptions,
    isLoading: userOptionsLoading,
  } = useUserSelectOptions();

  const upsertMutation = useUpsertSalesConfigurationMutation();
  const deleteMutation = useDeleteSalesConfigurationMutation();

  const handleOpenModal = useCallback((config?: ISalesConfiguration) => {
    if (config) {
      setEditingConfig(config);
      form.setFieldsValue({
        accountManagerId: userSelectOptions?.some((o: ISelectOption) => o.value === config.accountManagerId)
          ? config.accountManagerId
          : undefined,
        accountManagerCommissionRate: Number(config.accountManagerCommissionRate ?? 0) * 100,
        leadGeneratorId: userSelectOptions?.some((o: ISelectOption) => o.value === config.leadGeneratorId)
          ? config.leadGeneratorId
          : undefined,
        leadGeneratorCommissionRate: Number(config.leadGeneratorCommissionRate ?? 0) * 100,
        salesSupportId: userSelectOptions?.some((o: ISelectOption) => o.value === config.salesSupportId)
          ? config.salesSupportId
          : undefined,
        salesSupportCommissionRate: Number(config.salesSupportCommissionRate ?? 0) * 100,
      });
    } else {
      setEditingConfig(null);
      form.resetFields();
    }
    setIsModalOpen(true);
  }, [form, userSelectOptions]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingConfig(null);
    form.resetFields();
  }, [form]);

  const handleSave = async (values: {
    accountManagerId: string;
    accountManagerCommissionRate: number;
    leadGeneratorId: string;
    leadGeneratorCommissionRate: number;
    salesSupportId: string;
    salesSupportCommissionRate: number;
  }) => {
    try {
      await upsertMutation.mutateAsync({
        id: editingConfig?.id,
        accountManagerId: values.accountManagerId?.trim(),
        accountManagerCommissionRate: (values.accountManagerCommissionRate ?? 0) / 100,
        leadGeneratorId: values.leadGeneratorId?.trim(),
        leadGeneratorCommissionRate: (values.leadGeneratorCommissionRate ?? 0) / 100,
        salesSupportId: values.salesSupportId?.trim(),
        salesSupportCommissionRate: (values.salesSupportCommissionRate ?? 0) / 100,
      } as ISalesConfiguration);
      api.success({
        duration: 3,
        message: editingConfig ? "Sales Configuration Updated" : "Sales Configuration Created",
        description: `The sales configuration has been ${editingConfig ? "updated" : "created"} successfully.`,
      });
      setIsModalOpen(false);
      setEditingConfig(null);
      form.resetFields();
    } catch (error) {
      api.error({
        message: "Error",
        description: (error as Error)?.message || "Failed to save sales configuration. Please try again.",
      });
    }
  };

  const handleDelete = (config: ISalesConfiguration) => {
    const label = config.displayLabel || "this configuration";
    modal.confirm({
      title: "Delete Sales Configuration",
      content: `Are you sure you want to delete "${label}"? This cannot be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      icon: <ExclamationCircleOutlined />,
      cancelText: "Cancel",
      onOk: async () => {
        if (config.id) {
          try {
            await deleteMutation.mutateAsync(config.id);
            api.success({
              duration: 3,
              message: "Sales Configuration Deleted",
              description: "The sales configuration has been deleted successfully.",
            });
          } catch (error) {
            api.error({
              message: "Error",
              description: (error as Error)?.message || "Failed to delete sales configuration. Please try again.",
            });
          }
        }
      },
    });
  };

  const getUserName = (userId?: string) => {
    if (!userId) return "\u2014";
    const user = userSelectOptions?.find((o: ISelectOption) => o.value === userId);
    return user?.label || "Unknown";
  };

  const formatRate = formatCommissionRate;

  const columns = [
    {
      title: "Account Manager",
      key: "accountManager",
      render: (_: unknown, record: ISalesConfiguration) => (
        <span>
          {record.accountManagerName || getUserName(record.accountManagerId)}{" "}
          <Typography.Text type="secondary">
            ({formatRate(record.accountManagerCommissionRate)})
          </Typography.Text>
        </span>
      ),
    },
    {
      title: "Lead Generator",
      key: "leadGenerator",
      render: (_: unknown, record: ISalesConfiguration) => (
        <span>
          {record.leadGeneratorName || getUserName(record.leadGeneratorId)}{" "}
          <Typography.Text type="secondary">
            ({formatRate(record.leadGeneratorCommissionRate)})
          </Typography.Text>
        </span>
      ),
    },
    {
      title: "Sales Support",
      key: "salesSupport",
      render: (_: unknown, record: ISalesConfiguration) => (
        <span>
          {record.salesSupportName || getUserName(record.salesSupportId)}{" "}
          <Typography.Text type="secondary">
            ({formatRate(record.salesSupportCommissionRate)})
          </Typography.Text>
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_: unknown, record: ISalesConfiguration) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            Edit
          </Button>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            loading={deleteMutation.isPending}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];


  if (!configurationsSuccess && configurationsLoading) {
    return <Skeleton active />;
  }

  return (
    <div className="sales-configuration-setup">
      {notificationContextHolder}
      {contextHolder}

      <div className="sales-configuration-setup-header">
        <Typography.Title className="sales-configuration-setup-title">
          Sales Configuration Setup
        </Typography.Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenModal()}
        >
          Add Sales Configuration
        </Button>
      </div>

      <Typography.Paragraph>
        Manage sales configurations and their commission rates. Each configuration consists of an Account Manager,
        Lead Generator, and Sales Support with their respective commission rates.
      </Typography.Paragraph>

      <Table
        columns={columns}
        dataSource={configurationsData}
        loading={configurationsLoading}
        rowKey="id"
        pagination={false}
        size="middle"
        className="sales-configuration-setup-table"
      />

      <Modal
        title={editingConfig ? "Edit Sales Configuration" : "Add Sales Configuration"}
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        destroyOnClose
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          requiredMark={false}
          initialValues={{
            accountManagerCommissionRate: 0,
            leadGeneratorCommissionRate: 0,
            salesSupportCommissionRate: 0,
          }}
        >
          <div className="sales-configuration-setup-role-row">
            <Form.Item
              name="accountManagerId"
              label="Account Manager"
              className="sales-configuration-setup-role-select"
              rules={[
                { required: true, message: "Account Manager is required" },
              ]}
            >
              <Select
                placeholder="Select..."
                loading={userOptionsLoading}
                options={userSelectOptions?.map((o: ISelectOption) => ({
                  value: o.value,
                  label: o.label,
                })) || []}
                allowClear
              />
            </Form.Item>
            <Form.Item
              name="accountManagerCommissionRate"
              label="Rate"
              className="sales-configuration-setup-rate-input"
              rules={[{ required: true, message: "Required" }]}
            >
              <PercentageInput size="large" />
            </Form.Item>
          </div>

          <div className="sales-configuration-setup-role-row">
            <Form.Item
              name="leadGeneratorId"
              label="Lead Generator"
              className="sales-configuration-setup-role-select"
              rules={[
                { required: true, message: "Lead Generator is required" },
              ]}
            >
              <Select
                placeholder="Select..."
                loading={userOptionsLoading}
                options={userSelectOptions?.map((o: ISelectOption) => ({
                  value: o.value,
                  label: o.label,
                })) || []}
                allowClear
              />
            </Form.Item>
            <Form.Item
              name="leadGeneratorCommissionRate"
              label="Rate"
              className="sales-configuration-setup-rate-input"
              rules={[{ required: true, message: "Required" }]}
            >
              <PercentageInput size="large" />
            </Form.Item>
          </div>

          <div className="sales-configuration-setup-role-row">
            <Form.Item
              name="salesSupportId"
              label="Sales Support"
              className="sales-configuration-setup-role-select"
              rules={[
                { required: true, message: "Sales Support is required" },
              ]}
            >
              <Select
                placeholder="Select..."
                loading={userOptionsLoading}
                options={userSelectOptions?.map((o: ISelectOption) => ({
                  value: o.value,
                  label: o.label,
                })) || []}
                allowClear
              />
            </Form.Item>
            <Form.Item
              name="salesSupportCommissionRate"
              label="Rate"
              className="sales-configuration-setup-rate-input"
              rules={[{ required: true, message: "Required" }]}
            >
              <PercentageInput size="large" />
            </Form.Item>
          </div>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={upsertMutation.isPending}
              >
                {editingConfig ? "Update" : "Create"}
              </Button>
              <Button onClick={handleCloseModal}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
